import { SecurityService } from "../services/security/security.service";
import { useAuthStore } from "../../core/stores";
import { logger } from "../utils/logger";
/**
 * SECURITY CODES (Source of Truth)
 * Daftar kode keamanan yang disepakati antara FE dan BE.
 */
export var SecurityCode;
(function (SecurityCode) {
    SecurityCode["SESSION_EXPIRED"] = "ERR_SESSION_EXPIRED";
    SecurityCode["DEVICE_MISMATCH"] = "ERR_DEVICE_MISMATCH";
    SecurityCode["INVALID_JTI"] = "ERR_INVALID_JTI";
    SecurityCode["MFA_REQUIRED"] = "ERR_MFA_REQUIRED";
    SecurityCode["PASSWORD_EXPIRED"] = "PASSWORD_EXPIRED";
    SecurityCode["CAPTCHA_REQUIRED"] = "CAPTCHA_REQUIRED";
    SecurityCode["ACCOUNT_LOCKED"] = "ERR_ACCOUNT_LOCKED";
    SecurityCode["RATE_LIMIT"] = "ERR_RATE_LIMIT";
})(SecurityCode || (SecurityCode = {}));
let notifier = null;
let navigatorFn = null;
/**
 * AUTH HANDLER (Production Version)
 * Mengonsolidasikan deteksi, pemetaan, dan eksekusi keamanan.
 */
export const AuthHandler = {
    /**
     * Mendaftarkan fungsi notifikasi (misal: showToast dari UI library).
     */
    registerNotifier: (fn) => {
        notifier = fn;
    },
    /**
     * Mengirimkan notifikasi (toast) menggunakan notifier yang telah terdaftar.
     */
    notify: (title, message, type) => {
        if (notifier)
            notifier(title, message, type);
    },
    /**
     * Mendaftarkan fungsi navigasi (misal: useNavigate dari react-router).
     */
    registerNavigator: (fn) => {
        navigatorFn = fn;
    },
    /**
     * Melakukan navigasi global yang disinkronkan.
     */
    navigate: (path) => {
        if (navigatorFn) {
            navigatorFn(path);
        }
        else {
            window.location.href = path;
        }
    },
    /**
     * Ekstraksi kode keamanan dari berbagai format response backend.
     */
    getSecurityCode: (error) => {
        const data = error?.response?.data;
        // 1. Cek errorCode (Standard WebResponse)
        if (data?.errorCode)
            return data.errorCode;
        // 2. Cek message (Fallback untuk Filter-level rejections)
        if (typeof data?.message === "string") {
            const msg = data.message;
            if (msg.startsWith("ERR_"))
                return msg;
            if (msg === "Verifikasi CAPTCHA gagal.")
                return SecurityCode.CAPTCHA_REQUIRED;
        }
        return undefined;
    },
    /**
     * Mendapatkan pesan ramah pengguna untuk kode keamanan tertentu.
     */
    getSecurityMessage: (code) => {
        switch (code) {
            case SecurityCode.ACCOUNT_LOCKED:
                return "Akun Anda terkunci karena terlalu banyak percobaan login gagal. Silakan hubungi administrator.";
            case SecurityCode.PASSWORD_EXPIRED:
                return "Kata sandi Anda telah kedaluwarsa. Silakan perbarui kata sandi Anda untuk melanjutkan.";
            case SecurityCode.DEVICE_MISMATCH:
                return "Terdeteksi akses dari perangkat lain. Sesi Anda telah dihentikan demi keamanan.";
            case SecurityCode.RATE_LIMIT:
                return "Terlalu banyak permintaan dalam waktu singkat. Akses dibatasi sementara.";
            default:
                return "Terjadi masalah keamanan. Silakan login kembali.";
        }
    },
    /**
     * Orchestrator untuk tindakan keamanan global.
     * @param options.skipRedirect - Jika true, tidak akan melakukan redirect otomatis (misal saat di halaman Login).
     */
    handleGlobalError: (status, error, options = {}) => {
        const code = AuthHandler.getSecurityCode(error);
        const state = useAuthStore.getState();
        // 1. FAIL-CLOSED: Session Invalidation
        if (status === 401 ||
            code === SecurityCode.SESSION_EXPIRED ||
            code === SecurityCode.DEVICE_MISMATCH ||
            code === SecurityCode.INVALID_JTI) {
            // Pelaporan anomali jika terdeteksi indikasi pembajakan (Hijacking)
            if (code === SecurityCode.DEVICE_MISMATCH || code === SecurityCode.INVALID_JTI) {
                if (notifier) {
                    notifier("Sesi Terputus", AuthHandler.getSecurityMessage(code), "error");
                }
                SecurityService.reportAnomaly({
                    event: code,
                    userId: state.user?.username,
                    timestamp: Date.now(),
                    details: {
                        status,
                        path: window.location.pathname,
                        userAgent: navigator.userAgent
                    }
                });
            }
            state.clearSession();
            return;
        }
        // 2. RATE LIMITING & ACCOUNT LOCKOUT (Notification only)
        if (status === 429 || code === SecurityCode.ACCOUNT_LOCKED) {
            if (notifier) {
                const title = status === 429 ? "Akses Dibatasi" : "Akun Terkunci";
                notifier(title, AuthHandler.getSecurityMessage(code || "ERR_RATE_LIMIT"), "error");
            }
        }
        // 3. REDIRECTION LOGIC: MFA / Account Issues
        if (!options.skipRedirect) {
            if (code === SecurityCode.MFA_REQUIRED) {
                window.location.href = "/auth/mfa";
                return;
            }
            if (status === 403 || code === SecurityCode.ACCOUNT_LOCKED) {
                // Mencegah redirect jika kodenya adalah PASSWORD_EXPIRED karena harus ditangani di halaman Login
                if (code !== SecurityCode.PASSWORD_EXPIRED && code !== SecurityCode.CAPTCHA_REQUIRED) {
                    logger.error("🔴 [Security AuthHandler] Redirecting to /unauthorized due to 403 Forbidden:", {
                        status,
                        code,
                        url: error?.config?.url,
                        method: error?.config?.method,
                        headers: error?.config?.headers,
                        responseData: error?.response?.data,
                    });
                    // Show alert in development mode for easy debugging
                    if (import.meta.env?.DEV) {
                        alert(`[DEBUG 403] Akses Ditolak (403 Forbidden)\n\n` +
                            `Method: ${error?.config?.method?.toUpperCase()}\n` +
                            `API URL: ${error?.config?.url}\n\n` +
                            `Response Backend: ${JSON.stringify(error?.response?.data || "No response data")}`);
                    }
                    window.location.href = "/unauthorized";
                }
            }
        }
    }
};
